import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api';
import { recipes } from '../data/recipes';
import { parseServings } from './scale';
import type { CartItem } from './shopping';

// This week's plan: which recipes are being cooked and at what batch size.
// Shared like servings and progress, so the person who added the soup on
// their phone and the person building the cart on the laptop see one list.
// The server drops it after seven days.

export type PlanEntry = { servings: number | null; added_by: string | null };

type State = {
  entries: Record<string, PlanEntry | undefined>;
  add: (slug: string, servings?: number | null) => void;
  remove: (slug: string) => void;
  setServings: (slug: string, servings: number | null) => void;
  clear: () => void;
};

const Ctx = createContext<State>({ entries: {}, add: () => {}, remove: () => {}, setServings: () => {}, clear: () => {} });

function who(): string | undefined {
  return localStorage.getItem('cookbook-note-name') || undefined;
}

export function PlanProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<Record<string, PlanEntry | undefined>>({});

  useEffect(() => {
    api.plan().then(setEntries).catch(() => {});
  }, []);

  function add(slug: string, servings: number | null = null) {
    const prev = entries[slug];
    setEntries((o) => ({ ...o, [slug]: { servings, added_by: who() ?? null } }));
    api.setPlan(slug, servings, who()).catch(() => setEntries((o) => ({ ...o, [slug]: prev })));
  }
  function remove(slug: string) {
    const prev = entries[slug];
    setEntries((o) => ({ ...o, [slug]: undefined }));
    api.removePlan(slug).catch(() => setEntries((o) => ({ ...o, [slug]: prev })));
  }
  function setServings(slug: string, servings: number | null) {
    const prev = entries[slug];
    setEntries((o) => ({ ...o, [slug]: { servings, added_by: o[slug]?.added_by ?? null } }));
    api.setPlan(slug, servings).catch(() => setEntries((o) => ({ ...o, [slug]: prev })));
  }
  function clear() {
    const prev = entries;
    setEntries({});
    api.clearPlan().catch(() => setEntries(prev));
  }

  return <Ctx.Provider value={{ entries, add, remove, setServings, clear }}>{children}</Ctx.Provider>;
}

export function usePlan() {
  const s = useContext(Ctx);
  // Recipes in the plan, in registry order, with the factor the cart needs.
  const items: (CartItem & { servings: number | null; original: number | null })[] = [];
  for (const r of recipes) {
    const e = s.entries[r.slug];
    if (!e) continue;
    const original = parseServings(r.yield);
    const factor = original && e.servings ? e.servings / original : 1;
    items.push({ recipe: r, factor, servings: e.servings, original });
  }
  return { ...s, items, count: items.length, has: (slug: string) => !!s.entries[slug] };
}
